import {
  Cart,
  CartFactory,
  Checkout,
  CountriesFactory,
  GtinItem,
  ICartPosition,
  ICountriesLookup,
  PaymentFactory,
} from "@connox/typescript-library";
import {CurrencyCode} from "@connox/typescript-library/types/cart/CurrencyCode";
import * as React from "react";
import {PropsWithChildren, useEffect, useState} from "react";
import {UrlSearchParamsHelper} from "../../../helpers/urlSearchParamsHelper";
import {getShopId} from "../../lib/getShopId";
import window from "../../lib/WindowExtended";
import {IProductTrackingData, Tracking} from "../../../tracking/tracking";
import {ICartPromotion} from "@connox/typescript-library/types/cart/CartPromotion";
import {sentryAddBreadcrumb} from "../../../helpers/sentryHelper";
import {getUtilizationMessage} from "../../../helpers/utilizationMessageHelper";
import {CartErrorType, retrieveErrorMessages} from "../../../helpers/errorHandling";
import {ICartRequestHandlerPutCartOptions} from "@connox/typescript-library/types/cart/requestHandlers/CartRequestHandler";
import {useCustomerSelector} from "../../../store";
import {BrowserStorage} from "../../../storage/BrowserStorage";
import {ClientTokenType, PayPalInfoType} from "../../../helpers/paypal/paypalTypes";

interface ICurrencySelector extends HTMLFormElement {
  CurrencyCode: HTMLSelectElement;
}

export interface IViewState {
  isRegistering: boolean;
  shippingErrors: string[];
  paypalInfos: PayPalInfoType | undefined;
}

interface ICartContext {
  cart: Cart;

  isIntoCheckout: boolean;
  setIsIntoCheckout: (isIntoCheckout: boolean, checkout?: Checkout) => void;

  isCartVisible: boolean;
  setIsCartVisible: (isCartVisible: boolean) => void;

  itemsAdded: GtinItem[];
  setItemsAdded: (itemIds: GtinItem[]) => void;

  setViewState: (viewState?: IViewState) => void;
  viewState: IViewState;

  setVoucherAdded: (voucherAdded: string) => void;
  voucherAdded: string;

  utilizationMessage: string | undefined;

  setCartErrors: (errors: CartErrorType[]) => void;
  cartErrors: CartErrorType[];

  countries: ICountriesLookup[];

  braintreeToken: string | undefined;
}

interface IRequestCart {
  cart: Cart;
  done: () => void;
}

export interface ICartContextNoCart {
  requestCart: () => Promise<IRequestCart>;

  isCartVisible: boolean;
  setIsCartVisible: (isCartVisible: boolean) => void;

  setItemsAdded: (itemIds: GtinItem[]) => void;
  itemsAdded: GtinItem[];

  setVoucherAdded: (voucherAdded: string) => void;
  voucherAdded: string;
}

type IUnsafeCartContext = ICartContext | ICartContextNoCart | null;

const unsafeCartContext = React.createContext<IUnsafeCartContext>(null);

export const isCart = (cart: IUnsafeCartContext): cart is ICartContext => {
  return (cart as ICartContext).cart !== undefined;
};

export const useCartContext = (): ICartContext => {
  const cartContext = React.useContext(unsafeCartContext);
  if (!isCart(cartContext)) {
    throw new Error("useCartContext must be used in a component within a CartContextProvider.");
  }

  return cartContext;
};

export const useUnsafeCartContext = (): IUnsafeCartContext => {
  return React.useContext(unsafeCartContext);
};

const braintreeStorage = new BrowserStorage<string>({
  type: "sessionStorage",
  dataIdentifier: "braintree",
  hoursToCache: 1,
});
const braintreeEntryName = "clientId";

const CartContextProvider: React.FC<PropsWithChildren> = (props) => {
  const setItemsAdded = (itemsAdded: GtinItem[]) => {
    setState((prevState) => {
      if (!prevState) {
        return null;
      }

      return {...prevState, itemsAdded};
    });
  };
  /**
   * sets added voucher, to open the popup
   * @param voucherAdded voucher code string that was added to trigger voucher popup
   */
  const setVoucherAdded = (voucherAdded: string) => {
    setState((prevState) => {
      if (!prevState) {
        return null;
      }

      return {...prevState, voucherAdded};
    });
  };

  const setIsIntoCheckout = (isIntoCheckout: boolean, checkout?: Checkout) => {
    if (
      !isIntoCheckout &&
      checkout?.cartData.positions.find((pos) => pos.type === "Shipping")?.reference?.shippingClassId !==
        cartRef.current?.positions.find((pos) => pos.type === "Shipping")?.reference?.shippingClassId
    ) {
      void cartRef.current?.put({}).then(() => setPartialCartContextState({isIntoCheckout}));
    } else {
      setPartialCartContextState({isIntoCheckout, cartErrors: []});
    }
  };

  const setCartErrors = (cartErrors: CartErrorType[]) => setPartialCartContextState({cartErrors});

  /**
   * get the set currency, if respective form is shown
   * most probably the case in sales channel 100005 / connox.com
   */
  const getCurrencyFromModal = (): CurrencyCode | undefined => {
    const selectCurrencyForm = document.querySelector<ICurrencySelector>("#SelectCurrencyModal > form");
    if (!selectCurrencyForm) {
      return undefined;
    }
    const preselectedCurrency = selectCurrencyForm.CurrencyCode.selectedOptions[0].value;
    if (!preselectedCurrency) {
      return undefined;
    }

    return preselectedCurrency as CurrencyCode;
  };

  /**
   * get promotion params if existing
   */
  const getPromotionData = (): ICartPromotion | undefined => {
    if (!window.c_PromotionId) {
      return undefined;
    }

    return {
      id: Number(window.c_PromotionId),
      reference: window.c_PromotionContentReference || undefined,
    };
  };

  // Downloads and sets the bt token in here, above the `PayPalScriptProvider`,
  // so then, the `BraintreeButtonLoader` (below/inside the `PayPalScriptProvider`)
  // can dispatch it into the `PayPalScriptProvider` options
  const requestBraintreeToken = async () => {
    await PaymentFactory.getBraintreeToken(window.c_ActiveCurrency).then(({clientToken}) => {
      const paypalClientId = (JSON.parse(atob(clientToken)) as ClientTokenType).paypal.clientId;
      braintreeStorage.write(braintreeEntryName, paypalClientId);
      setState((prevState) => {
        if (!prevState) {
          return null;
        }

        return {...prevState, braintreeToken: clientToken, paypalClientId};
      });
    });
  };

  /**
   * update currency code and promotion id if necessary
   * @param cart
   */
  const updateCartSettings = async (cart: Cart) => {
    const promotion = getPromotionData();
    const preselectedCurrency = getCurrencyFromModal();
    const {isCorporateCustomer} = customer;
    const cartParams: ICartRequestHandlerPutCartOptions = {};

    if (preselectedCurrency && preselectedCurrency !== cart.currencyCode) {
      cartParams.currencyCode = preselectedCurrency;
    }
    if (promotion && promotion.id !== cart.promotion?.id) {
      cartParams.promotion = promotion;
    }
    if (cart.isCorporateCustomer !== isCorporateCustomer) {
      cartParams.isCorporateCustomer = isCorporateCustomer;
    }
    if (Object.keys(cartParams).length) {
      await cart.put(cartParams);
    }
  };

  const cartContextNoCart: ICartContextNoCart = {
    requestCart: async (): Promise<IRequestCart> => {
      const activeCountry = window.c_ActiveCountry;
      const activeCurrency = getCurrencyFromModal() ?? (window.c_ActiveCurrency as CurrencyCode);
      const promotionData = getPromotionData();

      const cartPromise = CartFactory.generateCart(getShopId(), customer.visitorId, {
        countryCode: activeCountry,
        currencyCode: activeCurrency,
        promotion: promotionData,
        isCorporateCustomer: customer.isCorporateCustomer,
      });

      const countriesPromise = CountriesFactory.getAllCountries(getShopId())
        .then((countries) => countries.filter((country) => country.isDeliveryAllowed))
        .catch(() => []);

      const utilizationMessagePromise = getUtilizationMessage().catch(() => undefined);

      return await Promise.all([cartPromise, countriesPromise, utilizationMessagePromise]).then(
        ([cart, countries, utilizationMessage]) => {
          // this must be called by the callback once the fallback is done
          const done = () => {
            updateCart(cart, countries, utilizationMessage);
          };

          return {cart, done};
        },
      );
    },
    setItemsAdded,
    itemsAdded: [],

    setVoucherAdded,
    voucherAdded: "",

    setIsCartVisible: (isCartVisible: boolean) =>
      setState((prevState) => {
        if (!prevState) {
          return null;
        }

        if (isCartVisible && isCart(prevState) && !prevState.braintreeToken) {
          void requestBraintreeToken();
        }

        return {...prevState, isCartVisible};
      }),
    isCartVisible: false,
  };

  const [state, setState] = useState<IUnsafeCartContext>(cartContextNoCart);
  const {customer} = useCustomerSelector();

  // We need this ref to use it in clean up useEffect callback
  const cartRef = React.useRef<Cart | null>(null);

  const setPartialCartContextState = (partialState: Partial<ICartContext>) => {
    setState((previousState) => {
      if (!isCart(previousState)) {
        return null;
      }
      if (partialState.isCartVisible && !previousState.braintreeToken) {
        void requestBraintreeToken();
      }

      return {...previousState, ...partialState};
    });
  };

  const trackCartItems = (positions: ICartPosition[]) => {
    const positionsToTrack = positions.reduce<IProductTrackingData[]>((akkumulator, current) => {
      if (current.type === "Item" && !!current.item && !!current.reference?.itemId) {
        akkumulator.push({
          name: current.text,
          id: current.item.itemId,
          brand: current.item.manufacturer,
          price: current.price.amount,
          quantity: current.quantity,
        });
      }

      return akkumulator;
    }, []);

    Tracking.setCartItems(positionsToTrack);
  };

  const refreshCart = () => {
    if (cartRef.current) {
      trackCartItems(cartRef.current.positions);
      const cartErrors = retrieveErrorMessages(cartRef.current);
      setPartialCartContextState({cartErrors});
    } else {
      setPartialCartContextState({});
    }
  };

  useEffect(() => {
    const requestCartForParams = async () => {
      await cartContextNoCart.requestCart().then(async ({cart, done}) => {
        const itemsAdded: GtinItem[] = UrlSearchParamsHelper.itemIds.map((item) => ({
          itemId: item.itemId,
          quantity: item.quantity,
          gtin: null,
        }));
        let voucherAdded = "";
        await cart.addMultipleItems(itemsAdded);

        if (UrlSearchParamsHelper.voucherCode) {
          voucherAdded = UrlSearchParamsHelper.voucherCode;
          await cart.addVoucher(UrlSearchParamsHelper.voucherCode);
        }

        done();

        if (state && (itemsAdded.length > 0 || voucherAdded !== "")) {
          setState((prevState) => {
            if (!prevState) {
              return null;
            }

            return {...prevState, itemsAdded, voucherAdded};
          });
        }
      });
    };

    const getCartIfExisting = async () => {
      /* starting three independent, parallel async requests */

      // request cart and possibly update its settings, if cart exists
      const possibleCart = await CartFactory.getCartForVisitorId(getShopId(), customer.visitorId).then(
        async (existingCart) => {
          if (!existingCart) {
            return null;
          }

          await updateCartSettings(existingCart);

          return existingCart;
        },
      );

      if (!possibleCart) {
        return;
      }

      // request delivery countries for current shop
      const countriesPromise = CountriesFactory.getAllCountries(getShopId())
        .then((countries) => countries.filter((country) => country.isDeliveryAllowed))
        .catch(() => []);

      //  request possible utilizationMessage
      const utilizationMessagePromise = getUtilizationMessage().catch(() => undefined);

      // wait for all requests to return
      await Promise.all([possibleCart, countriesPromise, utilizationMessagePromise]).then(
        ([existingCart, countries, utilizationMessage]) => {
          if (!existingCart) {
            return;
          }

          updateCart(existingCart, countries, utilizationMessage);
        },
      );
    };

    if (UrlSearchParamsHelper.containsParams()) {
      void requestCartForParams();
    } else {
      void getCartIfExisting();
    }

    customer.emitter.on(getCartIfExisting);

    // clean up of event listeners to prevent setting state in unmounted components
    return () => {
      customer.emitter.off(getCartIfExisting);
      if (!cartRef.current) {
        return;
      }
      cartRef.current.emitter.off(refreshCart);
    };
  }, []);

  useEffect(() => {
    if (state?.isCartVisible) {
      Tracking.sendEvent("trackCartPopupOpened");
    }
  }, [state?.isCartVisible]);

  useEffect(() => {
    if (state && state.itemsAdded.length && isCart(state)) {
      const addedPositions = state.cart.positions
        .filter<ICartPosition>(
          (pos): pos is ICartPosition =>
            pos.type === "Item" &&
            !!pos.reference?.itemId &&
            !!pos.item &&
            state.itemsAdded.some(
              ({itemId, gtin}) => itemId === pos.item?.itemId || gtin === (pos.item?.registrationNumber ?? undefined),
            ),
        )
        .map<IProductTrackingData>((pos) => ({
          brand: pos.item?.manufacturer ?? null,
          id: pos.reference?.itemId ?? 0,
          name: pos.text,
          price: pos.price.amount,
          quantity: pos.quantity,
        }));

      if (addedPositions.length) {
        Tracking.sendAddToCart(addedPositions, state.cart.currencyCode);
      }
    }
  }, [state?.itemsAdded.length]);

  // this is only called when the cart is being initialized
  const updateCart = (cart: Cart, countries: ICountriesLookup[], utilizationMessage?: string): void => {
    cart.emitter.on(refreshCart);
    cartRef.current = cart;
    trackCartItems(cart.positions);

    setState((prevState) => {
      return {
        cart,

        // the following is not really my doing
        isIntoCheckout: prevState && isCart(prevState) ? prevState.isIntoCheckout : false,
        setIsIntoCheckout,

        isCartVisible: !!prevState?.isCartVisible,
        setIsCartVisible: (isCartVisible: boolean) => {
          sentryAddBreadcrumb(
            isCartVisible ? "opening cart" : "closing cart",
            null,
            "setIsCartVisible",
            undefined,
            customer,
          );

          setPartialCartContextState({isCartVisible});
        },

        itemsAdded: prevState ? prevState.itemsAdded : [],
        setItemsAdded,

        setVoucherAdded,
        voucherAdded: prevState ? prevState.voucherAdded : "",

        setViewState: (viewState: IViewState | undefined) => setPartialCartContextState({viewState}),
        viewState: {isRegistering: false, shippingErrors: [], paypalInfos: undefined},

        utilizationMessage,

        cartErrors: retrieveErrorMessages(cart),
        setCartErrors,

        countries,

        braintreeToken: isCart(prevState) ? prevState.braintreeToken : undefined,
      };
    });
  };

  const {Provider} = unsafeCartContext;

  return <Provider value={state}>{props.children}</Provider>;
};

export default CartContextProvider;
